import { useEffect, useId, useRef, useState } from "react";
import type { CSSProperties, FocusEvent, ReactNode } from "react";
import { useMsal } from "@azure/msal-react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftRight, ChevronDown, Eye, History, LogOut, Moon, Sun, User } from "lucide-react";
import { clearLocalSession } from "../api/client";
import type { CurrentUser } from "../auth/useCurrentUser";
import { useBranding } from "../context/BrandingContext";
import { useThemeMode } from "../context/ThemeContext";
import { theme, floatingSheet } from "../theme";
import { ExperienceModePill } from "./ExperienceModeToggle";
import type { NavRoleTone, WorkspaceSwitch } from "./types";

// ── User menu ─────────────────────────────────────────────────────────────
// Initials avatar -> name, email, role pill, organisation / firm / bureau;
// workspace switches; experience mode; theme; profile; version history; sign
// out. Sign out is the one implementation for every shell.

export interface UserMenuProps {
  user: CurrentUser;
  switches: WorkspaceSwitch[];
  showExperienceMode: boolean;
  /** My Profile (client) / Profile & Security (auditor). Hidden when absent. */
  profileTo?: string;
  /** The shell's version-history route. */
  versionTo: string;
  version: string | null;
  /** Hide the name next to the avatar on narrow screens. */
  compact?: boolean;
}

export function initialsOf(name: string | null | undefined, fallback: string | null | undefined): string {
  const source = (name && name.trim()) || (fallback && fallback.trim()) || "?";
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function roleTone(role: CurrentUser["role"]): NavRoleTone {
  return role;
}

export function roleLabel(user: CurrentUser): string {
  if (user.role === "auditor") return "Auditor";
  if (user.role === "consultant") return "Consultant";
  if (user.actingAsOwnOrg) return "Own organisation";
  return "Client";
}

export function rolePillStyle(tone: NavRoleTone): CSSProperties {
  if (tone === "consultant") return { background: "var(--c-consultantBg)", color: "var(--c-consultant)" };
  if (tone === "auditor") return { background: "var(--c-auditorBg)", color: "var(--c-auditor)" };
  return { background: "var(--c-accentBg)", color: "var(--c-accentText)" };
}

/** The one sign-out for every shell (docs/NAVIGATION.md section 2). */
export function useSignOut(): () => void {
  const { instance } = useMsal();
  const navigate = useNavigate();
  return () => {
    navigate("/signedout");
    // A local session (auditor, Google SSO, or a consultant inside their own
    // organisation's portal) is revoked; an Entra session behind it is ended too.
    clearLocalSession();
    if (instance.getAllAccounts().length > 0) {
      instance.logoutRedirect({ onRedirectNavigate: () => false });
    }
  };
}

const THEME_LABEL: Record<string, { label: string; icon: ReactNode; next: string }> = {
  dark:       { label: "Dark theme",        icon: <Moon size={14} />, next: "Switch to light theme" },
  light:      { label: "Light theme",       icon: <Sun size={14} />,  next: "Switch to colourblind-friendly theme" },
  colorblind: { label: "Colourblind theme", icon: <Eye size={14} />,  next: "Switch to dark theme" },
};

function MenuRow({ icon, children, onClick, to, danger }: {
  icon?: ReactNode; children: ReactNode; onClick?: () => void; to?: string; danger?: boolean;
}) {
  const [hov, setHov] = useState(false);
  const style: CSSProperties = {
    display: "flex", alignItems: "center", gap: 9, width: "100%",
    padding: "8px 12px", border: "none", background: hov ? "var(--c-panelAlt)" : "transparent",
    color: danger ? "var(--c-dangerText)" : hov ? "var(--c-text)" : "var(--c-muted)",
    fontSize: 12.5, fontWeight: 500, textAlign: "left", textDecoration: "none",
    cursor: "pointer", fontFamily: theme.font, transition: "background 0.11s, color 0.11s",
  };
  const inner = (
    <>
      {icon && <span style={{ display: "flex", flexShrink: 0, opacity: 0.85 }}>{icon}</span>}
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </>
  );
  if (to) {
    return (
      <Link to={to} onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={style}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={style}>
      {inner}
    </button>
  );
}

function SectionCaption({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: "8px 12px 3px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--c-faint)" }}>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: "var(--c-border)", margin: "4px 0" }} />;
}

export function UserMenu({ user, switches, showExperienceMode, profileTo, versionTo, version, compact = false }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const popupId = useId();
  const { branding } = useBranding();
  const { mode, cycle } = useThemeMode();
  const signOut = useSignOut();

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { setOpen(false); buttonRef.current?.focus(); }
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Focus moves into the popover on open (the first row) and Tab past its last
  // control closes it, so the keyboard never leaves it open behind the page.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => popupRef.current?.querySelector<HTMLElement>("a, button")?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!wrapperRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
  };

  const close = () => setOpen(false);
  const initials = initialsOf(user.displayName, user.email);
  const tone = roleTone(user.role);
  const orgLine = user.role === "auditor"
    ? user.bureauName ?? null
    : user.role === "consultant"
      ? branding?.name ?? null
      : user.organizationName ?? null;
  const themeInfo = THEME_LABEL[mode] ?? THEME_LABEL.dark;

  return (
    <div ref={wrapperRef} onBlur={onBlur} style={{ position: "relative" }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Account menu for ${user.displayName || user.email}`}
        aria-haspopup="dialog"
        aria-controls={open ? popupId : undefined}
        aria-expanded={open}
        style={{
          display: "flex", alignItems: "center", gap: 7,
          background: open ? "var(--c-panelAlt)" : "transparent",
          border: "1px solid var(--c-border)", borderRadius: 2,
          padding: compact ? 2 : "2px 8px 2px 2px", cursor: "pointer",
          color: "var(--c-muted)", fontFamily: theme.font,
          transition: "background 0.12s, border-color 0.12s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--c-borderStrong)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--c-border)"; }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10.5, fontWeight: 800, letterSpacing: "0.02em", ...rolePillStyle(tone),
          }}
        >
          {initials}
        </span>
        {!compact && (
          <span style={{ fontSize: 12.5, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {user.displayName || user.email}
          </span>
        )}
        {!compact && <ChevronDown size={13} style={{ opacity: 0.7, flexShrink: 0 }} />}
      </button>

      {open && (
        <div
          ref={popupRef}
          id={popupId}
          role="dialog"
          aria-label="Account"
          style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0, width: 264,
            ...floatingSheet, zIndex: 2000, overflow: "hidden",
            fontFamily: theme.font, paddingBottom: 4,
          }}
        >
          {/* Identity */}
          <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "12px 12px 10px", borderBottom: "1px solid var(--c-border)" }}>
            <span
              aria-hidden="true"
              style={{
                width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12.5, fontWeight: 800, flexShrink: 0, ...rolePillStyle(tone),
              }}
            >
              {initials}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.displayName || user.email}
              </div>
              <div style={{ fontSize: 11, color: "var(--c-subtle)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.email}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, minWidth: 0 }}>
                <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", flexShrink: 0, ...rolePillStyle(tone) }}>
                  {roleLabel(user)}
                </span>
                {orgLine && (
                  <span style={{ fontSize: 10.5, color: "var(--c-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {orgLine}
                  </span>
                )}
              </div>
            </div>
          </div>

          {switches.length > 0 && (
            <>
              <SectionCaption>Switch workspace</SectionCaption>
              {switches.map((s) => (
                <MenuRow key={s.id} icon={<ArrowLeftRight size={14} />} onClick={() => { close(); s.onSelect(); }}>
                  <div style={{ lineHeight: 1.25 }}>{s.label}</div>
                  {s.description && <div style={{ fontSize: 10.5, color: "var(--c-faint)", marginTop: 1 }}>{s.description}</div>}
                </MenuRow>
              ))}
              <Divider />
            </>
          )}

          {showExperienceMode && (
            <>
              <SectionCaption>Experience mode</SectionCaption>
              <div style={{ padding: "2px 12px 8px" }}>
                <ExperienceModePill />
              </div>
              <Divider />
            </>
          )}

          <MenuRow icon={themeInfo.icon} onClick={cycle}>
            <div style={{ lineHeight: 1.25 }}>{themeInfo.label}</div>
            <div style={{ fontSize: 10.5, color: "var(--c-faint)", marginTop: 1 }}>{themeInfo.next}</div>
          </MenuRow>
          {profileTo && (
            <MenuRow icon={<User size={14} />} to={profileTo} onClick={close}>
              {user.role === "auditor" ? "Profile & Security" : "My Profile"}
            </MenuRow>
          )}
          <MenuRow icon={<History size={14} />} to={versionTo} onClick={close}>
            Version history{version ? <span style={{ color: "var(--c-faint)", marginLeft: 6, fontSize: 11 }}>v{version}</span> : null}
          </MenuRow>
          <Divider />
          <MenuRow icon={<LogOut size={14} />} onClick={() => { close(); signOut(); }} danger>
            Sign out
          </MenuRow>
        </div>
      )}
    </div>
  );
}
